"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import React, { useCallback, useMemo, useState } from "react";
import AddDoseWrapper from "@/components/dose/add-dose-wrapper";
import { DETAILED_DOSE_TABLE_COLUMN_COUNT, DetailedDoseTableHead, DetailedDoseTableRow } from "@/components/dose/detailed-dose-table";
import { DoseLogTable } from "@/components/dose/dose-log-table/dose-log-table";
import SignOffDoseModal from "@/components/dose/sign-off-dose-modal";
import { Badge, Button } from "@/components/ui";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { useTenant } from "@/contexts/tenant-provider";
import { useUser } from "@/contexts/user-provider";
import { useClinicClock } from "@/hooks/use-clinic-clock";
import { useDoseLogTable } from "@/hooks/use-dose-log-table";
import { useNoteDoseScope } from "@/hooks/use-note-dose-scope";
import { getDoseComponentSummary, getDoseComponents, layoutDoseRowsByMedication } from "@/lib/dose-components";
import { shouldShowMedicationMissingFieldWarnings } from "@/lib/tenant-flags";
import { MedicationSummary } from "@/types/medication";
import { NoteAnswerDetail, NoteDetail } from "@/types/notes";
import { useSignOffMedicationMutation } from "@/api/provider/user/patient/patient-mutation";
import { patientQueryOptions } from "@/api/provider/user/patient/patient-query";
interface MedicationAnswerEditorProps {
  answer: NoteAnswerDetail;
  onChange: (answerId: string, data: Record<string, any>, noteAttachmentId?: string) => void;
  disabled?: boolean;
  noteId?: string;
  patientId?: string;
}
export const MedicationAnswerEditor: React.FC<MedicationAnswerEditorProps> = t0 => {
  const $ = _c(60);
  const {
    answer,
    onChange,
    disabled: t1,
    noteId,
    patientId
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const {
    tenant
  } = useTenant();
  const {
    currentUser
  } = useUser();
  const {
    isOn: showsDoseLogTable
  } = useDoseLogTable();
  const clock = useClinicClock();
  const [pendingSignOffMedication, setPendingSignOffMedication] = useState(null);
  const signOffMedicationMutation = useSignOffMedicationMutation();
  let t2;
  if ($[0] !== currentUser?.id || $[1] !== patientId || $[2] !== pendingSignOffMedication || $[3] !== signOffMedicationMutation) {
    t2 = async () => {
      if (!pendingSignOffMedication || !patientId) {
        return;
      }
      await signOffMedicationMutation.mutateAsync({
        medicationId: pendingSignOffMedication.id,
        patientId,
        currentProviderId: currentUser?.id
      });
      setPendingSignOffMedication(null);
    };
    $[0] = currentUser?.id;
    $[1] = patientId;
    $[2] = pendingSignOffMedication;
    $[3] = signOffMedicationMutation;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  currentUser?.id;
  const handleSignOffConfirm = t2;
  let t3;
  if ($[5] !== patientId) {
    t3 = patientQueryOptions.detail(patientId || "");
    $[5] = patientId;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = !!patientId;
  let t5;
  if ($[7] !== t3 || $[8] !== t4) {
    t5 = {
      ...t3,
      enabled: t4
    };
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const {
    data: patient,
    isLoading: isLoadingPatient
  } = useQuery(t5);
  const {
    noteDetail,
    isLoadingNote,
    noteIdsInChain,
    isDoseForNote
  } = useNoteDoseScope(noteId);
  let t6;
  if ($[10] !== answer.data.medications || $[11] !== answer.id || $[12] !== onChange) {
    t6 = medication => {
      const existingMedications = answer.data.medications || [];
      const medicationExists = existingMedications.some(med => med.name === getDoseComponentSummary(medication));
      if (!medicationExists) {
        const components = getDoseComponents(medication);
        const primaryComponent = components[0];
        const newMedication = {
          name: getDoseComponentSummary(medication),
          amount: primaryComponent.amount ?? null,
          unit: primaryComponent.unit ?? null,
          frequency: medication.frequency ?? null,
          quantity: primaryComponent.quantity ?? 1
        };
        onChange(answer.id, {
          medications: [...existingMedications, newMedication]
        });
      }
    };
    $[10] = answer.data.medications;
    $[11] = answer.id;
    $[12] = onChange;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const handleMedicationAdd = t6;
  let t7;
  if ($[14] !== patient?.medications) {
    t7 = patient?.medications?.filter(_temp) || [];
    $[14] = patient?.medications;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const allMedications = t7;
  let t8;
  if ($[16] !== allMedications || $[17] !== isDoseForNote) {
    t8 = allMedications.filter(isDoseForNote);
    $[16] = allMedications;
    $[17] = isDoseForNote;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const dosesForThisNote = t8;
  let t9;
  if ($[19] !== clock || $[20] !== dosesForThisNote) {
    t9 = layoutDoseRowsByMedication(dosesForThisNote, clock);
    $[19] = clock;
    $[20] = dosesForThisNote;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const doseLayout = t9;
  let t10;
  if ($[22] !== doseLayout || $[23] !== noteDetail || $[24] !== noteIdsInChain || $[25] !== patient || $[26] !== tenant) {
    t10 = medication_0 => {
      if (!patient) {
        return null;
      }
      const showMissingFieldWarnings = shouldShowMedicationMissingFieldWarnings({
        tenant,
        medicationNoteId: medication_0.noteId,
        noteIdsInChain
      });
      const components_0 = getDoseComponents(medication_0);
      return <DetailedDoseTableRow key={medication_0.id} summary={doseLayout.headerBefore.get(medication_0.id)} medication={medication_0} patient={patient} selectedNote={noteDetail ? noteDetail as NoteDetail : undefined} onRequestSignOff={setPendingSignOffMedication}>{showMissingFieldWarnings && <>{components_0.some(_temp2) && <Badge variant="warning" title="Edit dose to add">Missing Lot #</Badge>}{!medication_0.administrationLocation && <Badge variant="warning" title="Edit dose to add">Missing Site</Badge>}</>}</DetailedDoseTableRow>;
    };
    $[22] = doseLayout;
    $[23] = noteDetail;
    $[24] = noteIdsInChain;
    $[25] = patient;
    $[26] = tenant;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  const renderDetailedRow = t10;
  const isLoading = isLoadingPatient || isLoadingNote;
  const rendersDoseLog = !isLoading && showsDoseLogTable && !!patient;
  let t11;
  if ($[28] !== disabled || $[29] !== handleMedicationAdd || $[30] !== noteDetail || $[31] !== patient || $[32] !== patientId) {
    t11 = !disabled && patientId ? <AddDoseWrapper patientId={patientId} patient={patient} selectedNote={noteDetail || undefined} onSubmit={handleMedicationAdd}><Button variant="secondary" size="sm" type="button">Add Dose</Button></AddDoseWrapper> : null;
    $[28] = disabled;
    $[29] = handleMedicationAdd;
    $[30] = noteDetail;
    $[31] = patient;
    $[32] = patientId;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  const addDoseButton = t11;
  const MedicationTableSkeleton = _temp4;
  let t12;
  if ($[34] !== addDoseButton || $[35] !== isLoading || $[36] !== patient || $[37] !== showsDoseLogTable) {
    t12 = addDoseButton && !(showsDoseLogTable && (isLoading || patient)) && <div className="flex justify-end">{addDoseButton}</div>;
    $[34] = addDoseButton;
    $[35] = isLoading;
    $[36] = patient;
    $[37] = showsDoseLogTable;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] !== addDoseButton || $[40] !== disabled || $[41] !== doseLayout || $[42] !== dosesForThisNote || $[43] !== isLoading || $[44] !== noteDetail || $[45] !== patient || $[46] !== patientId || $[47] !== renderDetailedRow || $[48] !== rendersDoseLog) {
    t13 = !patientId ? <p className="text-body text-foreground-secondary">No patient selected</p> : isLoading ? <MedicationTableSkeleton /> : rendersDoseLog ? <DoseLogTable patient={patient} doses={dosesForThisNote} selectedNote={noteDetail} readOnly={disabled} defaultGroupByDate={false} emptyMessage="No doses for this note" actions={addDoseButton} /> : <div className="w-full overflow-x-auto rounded-lg"><Table className="w-full overflow-visible leading-normal md:table-fixed"><DetailedDoseTableHead /><TableBody>{dosesForThisNote.length > 0 ? doseLayout.rows.map(renderDetailedRow) : <TableRow selectable={false}><TableCell colSpan={DETAILED_DOSE_TABLE_COLUMN_COUNT} className="text-body text-foreground-secondary">No doses for this note</TableCell></TableRow>}</TableBody></Table></div>;
    $[39] = addDoseButton;
    $[40] = disabled;
    $[41] = doseLayout;
    $[42] = dosesForThisNote;
    $[43] = isLoading;
    $[44] = noteDetail;
    $[45] = patient;
    $[46] = patientId;
    $[47] = renderDetailedRow;
    $[48] = rendersDoseLog;
    $[49] = t13;
  } else {
    t13 = $[49];
  }
  let t14;
  if ($[50] !== handleSignOffConfirm || $[51] !== patient || $[52] !== pendingSignOffMedication || $[53] !== showsDoseLogTable || $[54] !== signOffMedicationMutation) {
    t14 = patient && !showsDoseLogTable && <SignOffDoseModal isOpen={!!pendingSignOffMedication} onClose={() => setPendingSignOffMedication(null)} onConfirm={handleSignOffConfirm} medication={pendingSignOffMedication} patient={patient} isLoading={signOffMedicationMutation.isPending} />;
    $[50] = handleSignOffConfirm;
    $[51] = patient;
    $[52] = pendingSignOffMedication;
    $[53] = showsDoseLogTable;
    $[54] = signOffMedicationMutation;
    $[55] = t14;
  } else {
    t14 = $[55];
  }
  let t15;
  if ($[56] !== t12 || $[57] !== t13 || $[58] !== t14) {
    t15 = <div className="space-y-3">{t12}{t13}{t14}</div>;
    $[56] = t12;
    $[57] = t13;
    $[58] = t14;
    $[59] = t15;
  } else {
    t15 = $[59];
  }
  return t15;
};
function _temp(med_0) {
  return med_0.provider;
}
function _temp2(component) {
  return !component.lots?.length;
}
function _temp3(_, index) {
  return <TableRow key={index} selectable={false}><TableCell><div className="flex items-center gap-2"><div className="h-4 w-32 animate-pulse rounded bg-muted" /><div className="h-4 w-16 animate-pulse rounded bg-muted" /></div></TableCell><TableCell><div className="space-y-1"><div className="h-4 w-24 animate-pulse rounded bg-muted" /><div className="h-3 w-20 animate-pulse rounded bg-muted" /></div></TableCell><TableCell><div className="h-4 w-28 animate-pulse rounded bg-muted" /></TableCell><TableCell><div className="space-y-1"><div className="h-4 w-20 animate-pulse rounded bg-muted" /><div className="h-3 w-16 animate-pulse rounded bg-muted" /></div></TableCell><TableCell><div className="h-8 w-8 animate-pulse rounded bg-muted" /></TableCell></TableRow>;
}
function _temp4() {
  return <div className="w-full overflow-x-auto rounded-lg"><Table className="w-full overflow-visible leading-normal md:table-fixed"><DetailedDoseTableHead /><TableBody>{Array.from({
          length: 2
        }).map(_temp3)}</TableBody></Table></div>;
}